/**
 * @file     contactFields.ts
 * @readme   ./CLAUDE.md
 * @purpose  The one contact contract every lead-capture surface collects:
 *           first name, last name, email and mobile phone, all required.
 *           Client forms validate and build their payload here, and the
 *           /api/leads route checks the same rules, so a surface cannot drift
 *           back to email-only without failing both.
 */
import { toE164 } from '@/utils/formatPhone';

export interface ContactValues {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

export type ContactField = keyof ContactValues;
export type ContactErrors = Partial<Record<ContactField, string>>;

export const EMPTY_CONTACT: ContactValues = { firstName: '', lastName: '', email: '', phone: '' };

export const CONTACT_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Translate = (key: string, options: { defaultValue: string }) => string;

/** Plain labels shared by every surface. */
export function contactLabels(t: Translate): Record<ContactField, string> {
  return {
    firstName: t('forms.contact.firstNameLabel', { defaultValue: 'First name' }),
    lastName: t('forms.contact.lastNameLabel', { defaultValue: 'Last name' }),
    email: t('forms.contact.emailLabel', { defaultValue: 'Email' }),
    phone: t('forms.contact.phoneLabel', { defaultValue: 'Mobile phone' }),
  };
}

/**
 * "Jordan Lee Smith" → first "Jordan", last "Lee Smith". Used where a layout
 * keeps one full-name field; the CRM and the account still get first and last.
 */
export function splitFullName(full: string | null | undefined): { firstName: string; lastName: string } {
  const parts = (full ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: '', lastName: '' };
  return { firstName: parts[0]!, lastName: parts.slice(1).join(' ') };
}

/** Field errors for the four required contact fields. Empty object means valid. */
export function validateContact(
  values: ContactValues,
  t: Translate,
  fields: readonly ContactField[] = ['firstName', 'lastName', 'email', 'phone'],
): ContactErrors {
  const errors: ContactErrors = {};
  if (fields.includes('firstName') && !values.firstName.trim()) {
    errors.firstName = t('forms.contact.firstNameRequired', { defaultValue: 'Enter your first name.' });
  }
  if (fields.includes('lastName') && !values.lastName.trim()) {
    errors.lastName = t('forms.contact.lastNameRequired', { defaultValue: 'Enter your last name.' });
  }
  if (fields.includes('email') && !CONTACT_EMAIL_PATTERN.test(values.email.trim())) {
    errors.email = t('forms.contact.emailInvalid', { defaultValue: 'Enter an email address, like name@example.com.' });
  }
  if (fields.includes('phone') && !toE164(values.phone)) {
    errors.phone = t('forms.contact.phoneInvalid', { defaultValue: 'Enter a 10-digit mobile number.' });
  }
  return errors;
}

/**
 * The contact keys every lead payload carries. `name` stays for the existing
 * lead contract (FUB notes, the leads row); first and last travel separately so
 * the CRM never has to guess where a two-word first name ends.
 */
export function contactPayload(values: ContactValues): {
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
} {
  const firstName = values.firstName.trim();
  const lastName = values.lastName.trim();
  return {
    name: [firstName, lastName].filter(Boolean).join(' '),
    firstName,
    lastName,
    email: values.email.trim().toLowerCase(),
    phone: toE164(values.phone) ?? values.phone.trim(),
  };
}
